import { useState, useEffect, useMemo } from 'react'
import { investorApi } from '@/lib/api'
import toast from 'react-hot-toast'

interface AdsetPL {
  adset_id: string
  adset_name: string
  spend: number
  revenue: number
  pl: number
  clicks_meta: number
  clicks_levanta: number
  conversions: number
  commission_rate: number | null
}

interface CampaignPL {
  meta_campaign_id: string
  label: string | null
  investment_amount: number
  spend: number
  revenue: number
  pl: number
  adsets: AdsetPL[]
}

interface DashboardData {
  profit_share_percent: number
  total_invested: number
  total_spend: number
  total_revenue: number
  total_pl: number
  campaigns: CampaignPL[]
  date_start: string
  date_end: string
}

type DatePreset = 'today' | 'yesterday' | 'last7' | 'last30' | 'lifetime'

function formatDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function getPresetRange(preset: DatePreset): { startDate: string; endDate: string } {
  const end = new Date()
  end.setHours(0, 0, 0, 0)

  if (preset === 'today') {
    const formatted = formatDate(end)
    return { startDate: formatted, endDate: formatted }
  }

  if (preset === 'yesterday') {
    const start = new Date(end)
    start.setDate(start.getDate() - 1)
    const formatted = formatDate(start)
    return { startDate: formatted, endDate: formatted }
  }

  if (preset === 'last7') {
    const start = new Date(end)
    start.setDate(start.getDate() - 6)
    return { startDate: formatDate(start), endDate: formatDate(end) }
  }

  if (preset === 'last30') {
    const start = new Date(end)
    start.setDate(start.getDate() - 29)
    return { startDate: formatDate(start), endDate: formatDate(end) }
  }

  return { startDate: '1970-01-01', endDate: formatDate(end) }
}

function formatCurrency(v: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v)
}

function formatPct(v: number): string {
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`
}

export default function InvestorDashboardPage() {
  const initialRange = useMemo(() => {
    return getPresetRange('yesterday')
  }, [])

  const [selectedPreset, setSelectedPreset] = useState<DatePreset>('yesterday')
  const [startDate, setStartDate] = useState(initialRange.startDate)
  const [endDate, setEndDate] = useState(initialRange.endDate)
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(false)
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null)

  const fetchDashboard = async (range?: { startDate: string; endDate: string }) => {
    const activeStartDate = range?.startDate ?? startDate
    const activeEndDate = range?.endDate ?? endDate

    setLoading(true)
    try {
      const resp = await investorApi.getDashboard(activeStartDate, activeEndDate)
      setData(resp.data)
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboard(initialRange)
  }, [])

  const handlePresetChange = async (preset: DatePreset) => {
    if (loading && preset === selectedPreset) {
      return
    }
    const range = getPresetRange(preset)
    setSelectedPreset(preset)
    setStartDate(range.startDate)
    setEndDate(range.endDate)
    await fetchDashboard(range)
  }

  const plColor = (v: number) => (v >= 0 ? 'var(--inv-green)' : 'var(--inv-red)')
  const plBg = (v: number) => (v >= 0 ? 'var(--inv-green-bg)' : 'var(--inv-red-bg)')
  const roiPct = data && data.total_invested > 0 ? (data.total_pl / data.total_invested) * 100 : 0

  return (
    <div className="inv-dashboard">
      {/* Header */}
      <div className="inv-header">
        <div>
          <h1 className="inv-title">Investor Dashboard</h1>
          <p className="inv-subtitle">Daily reporting overview</p>
        </div>
      </div>

      {/* Lifetime invested summary */}
      <div className="inv-lifetime-card">
        <span className="inv-kpi-label">Total Invested (Lifetime)</span>
        <span className="inv-lifetime-value">
          {data ? formatCurrency(data.total_invested) : '—'}
        </span>
        {data && (
          <span className="inv-lifetime-note">
            Your profit share: {data.profit_share_percent.toFixed(2)}%
          </span>
        )}
      </div>

      {/* Date preset row */}
      <div className="inv-preset-row">
        {[
          { id: 'today', label: 'Today' },
          { id: 'yesterday', label: 'Yesterday' },
          { id: 'last7', label: 'Last 7' },
          { id: 'last30', label: 'Last 30' },
          { id: 'lifetime', label: 'Lifetime' },
        ].map((preset) => (
          <button
            key={preset.id}
            className={`inv-preset-btn ${selectedPreset === preset.id ? 'is-active' : ''}`}
            onClick={() => handlePresetChange(preset.id as DatePreset)}
            disabled={loading}
          >
            {preset.label}
          </button>
        ))}
      </div>
      <p className="inv-range-caption">
        Reporting period: {startDate} to {endDate}
      </p>

      {loading && !data && (
        <div className="inv-loading">
          <div className="inv-spinner" />
          <p>Loading your data...</p>
        </div>
      )}

      {data && (
        <>
          {/* KPI cards */}
          <div className="inv-kpi-grid">
            <div className="inv-kpi-card">
              <span className="inv-kpi-label">Ad Spend (Cost)</span>
              <span className="inv-kpi-value" style={{ color: 'var(--inv-red)' }}>
                {formatCurrency(data.total_spend)}
              </span>
            </div>
            <div className="inv-kpi-card">
              <span className="inv-kpi-label">Revenue</span>
              <span className="inv-kpi-value" style={{ color: 'var(--inv-green)' }}>
                {formatCurrency(data.total_revenue)}
              </span>
            </div>
            <div className="inv-kpi-card inv-kpi-card-highlight" style={{ borderColor: plColor(data.total_pl) }}>
              <span className="inv-kpi-label">Your Profit / Loss</span>
              <span className="inv-kpi-value" style={{ color: plColor(data.total_pl) }}>
                {formatCurrency(data.total_pl)}
              </span>
              <span className="inv-kpi-badge" style={{ background: plBg(data.total_pl), color: plColor(data.total_pl) }}>
                {formatPct(roiPct)} ROI
              </span>
            </div>
          </div>

          {/* P&L summary bar */}
          {data.total_spend > 0 && (
            <div className="inv-pl-bar-container">
              <div className="inv-pl-bar-header">
                <span>Cost vs Revenue</span>
              </div>
              <div className="inv-pl-bar-track">
                <div
                  className="inv-pl-bar-spend"
                  style={{ width: `${Math.min((data.total_spend / (data.total_spend + data.total_revenue)) * 100, 100)}%` }}
                />
                <div
                  className="inv-pl-bar-revenue"
                  style={{ width: `${Math.min((data.total_revenue / (data.total_spend + data.total_revenue)) * 100, 100)}%` }}
                />
              </div>
              <div className="inv-pl-bar-legend">
                <span><span className="inv-dot inv-dot-spend" /> Spend {formatCurrency(data.total_spend)}</span>
                <span><span className="inv-dot inv-dot-revenue" /> Revenue {formatCurrency(data.total_revenue)}</span>
              </div>
            </div>
          )}

          {/* Campaign breakdown */}
          <div className="inv-section">
            <h2 className="inv-section-title">Campaign Breakdown</h2>
            {data.campaigns.length === 0 && (
              <div className="inv-empty">No campaigns linked yet.</div>
            )}
            {data.campaigns.map((camp) => {
              const isExpanded = expandedCampaign === camp.meta_campaign_id
              return (
                <div key={camp.meta_campaign_id} className="inv-campaign-card">
                  <button
                    className="inv-campaign-header"
                    onClick={() => setExpandedCampaign(isExpanded ? null : camp.meta_campaign_id)}
                  >
                    <div className="inv-campaign-info">
                      <span className="inv-campaign-name">
                        {camp.label || camp.meta_campaign_id}
                      </span>
                      <span className="inv-campaign-invested">
                        Invested: {formatCurrency(camp.investment_amount / 100)}
                      </span>
                    </div>
                    <div className="inv-campaign-metrics">
                      <div className="inv-campaign-metric">
                        <span className="inv-metric-label">Spend</span>
                        <span className="inv-metric-value">{formatCurrency(camp.spend)}</span>
                      </div>
                      <div className="inv-campaign-metric">
                        <span className="inv-metric-label">Revenue</span>
                        <span className="inv-metric-value" style={{ color: 'var(--inv-green)' }}>
                          {formatCurrency(camp.revenue)}
                        </span>
                      </div>
                      <div className="inv-campaign-metric">
                        <span className="inv-metric-label">P&L</span>
                        <span className="inv-metric-value" style={{ color: plColor(camp.pl) }}>
                          {formatCurrency(camp.pl)}
                        </span>
                      </div>
                      <span className={`inv-chevron ${isExpanded ? 'inv-chevron-open' : ''}`}>
                        &#9662;
                      </span>
                    </div>
                  </button>

                  {isExpanded && camp.adsets.length > 0 && (
                    <div className="inv-adset-table-wrapper">
                      <table className="inv-adset-table">
                        <thead>
                          <tr>
                            <th>Ad Set</th>
                            <th>Spend</th>
                            <th>Revenue</th>
                            <th>P&L</th>
                            <th>Conversions</th>
                            <th>Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {camp.adsets.map((a) => (
                            <tr key={a.adset_id + a.adset_name}>
                              <td className="inv-adset-name">{a.adset_name}</td>
                              <td>{formatCurrency(a.spend)}</td>
                              <td style={{ color: 'var(--inv-green)' }}>{formatCurrency(a.revenue)}</td>
                              <td style={{ color: plColor(a.pl) }}>{formatCurrency(a.pl)}</td>
                              <td>{a.conversions}</td>
                              <td>{a.commission_rate !== null ? `${(a.commission_rate * 100).toFixed(1)}%` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {isExpanded && camp.adsets.length === 0 && (
                    <div className="inv-adset-empty">No adset data for this period.</div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
